import { Directive, forwardRef, inject, Injector, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { validateExpiry } from '../validators/expiry.validator';

/**
 * Expiry date validator directive for template-driven forms.
 * Validates credit card expiry dates with end-of-month awareness.
 *
 * Validation timing:
 * - Initial validation: on blur only (VAL-01)
 * - Re-validation after error: on valueChanges (clears instantly when valid)
 *
 * Usage:
 * ```html
 * <input appExpiryValidator [(ngModel)]="expiryDate" #ctrl="ngModel" />
 * @if (ctrl.errors?.expiry) {
 *   <span>{{ getExpiryErrorKey(ctrl.errors.expiry) | translate }}</span>
 * }
 * ```
 */
@Directive({
  selector: '[appExpiryValidator]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ExpiryValidatorDirective),
      multi: true,
    },
  ],
  host: {
    '(blur)': 'onBlur()',
  },
})
export class ExpiryValidatorDirective implements Validator, OnDestroy {
  private _injector = inject(Injector);
  private _ngControl: NgControl | null = null;
  private _valueChangesSub: Subscription | null = null;

  private _hasBlurred = false;
  private _lastError: ValidationErrors | null = null;

  /**
   * Lazily resolve NgControl to avoid circular dependency.
   * NgControl depends on validators, and this directive is a validator.
   */
  private get ngControl(): NgControl | null {
    if (!this._ngControl) {
      try {
        this._ngControl = this._injector.get(NgControl, null);
      } catch {
        this._ngControl = null;
      }
    }
    return this._ngControl;
  }

  ngOnDestroy(): void {
    this._valueChangesSub?.unsubscribe();
  }

  /**
   * Validator function called by Angular forms.
   */
  validate(control: AbstractControl): ValidationErrors | null {
    // Don't validate until user has blurred at least once
    if (!this._hasBlurred) {
      return null;
    }

    const error = validateExpiry(control.value);
    this._lastError = error ? { expiry: error } : null;
    return this._lastError;
  }

  /**
   * Handle blur event - trigger validation and subscribe to valueChanges.
   */
  onBlur(): void {
    this._hasBlurred = true;

    // Trigger validation update
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();

      // Subscribe to valueChanges to re-validate when value changes (hybrid approach)
      // This ensures we see the final synchronized value after all transformations
      if (!this._valueChangesSub) {
        this._valueChangesSub = this.ngControl.control.valueChanges.subscribe(() => {
          // Only re-validate if there was an error (to clear it when valid)
          if (this._lastError && this.ngControl?.control) {
            // Use emitEvent: false to prevent infinite loop - updateValueAndValidity
            // would otherwise emit valueChanges, triggering this subscription again
            this.ngControl.control.updateValueAndValidity({ emitEvent: false });
          }
        });
      }
    }
  }
}
